/**
 * Mac o PC, para el teclado: en Mac la tecla base de los atajos y sus
 * insignias son otras (`work/digitacion/keymap.ts`).
 *
 * «mac» es todo lo que tiene el teclado de Apple: macOS y el iPad con
 * teclado, donde ⌥ escribe caracteres y ⌘ es de las órdenes, igual que en
 * la Mac. Lo demás --Windows, Linux, ChromeOS-- es «pc».
 */
export type Platform = "mac" | "pc";

interface PlatformNavigator {
  readonly platform?: string;
  readonly userAgent?: string;
  /** Solo Chromium y en contexto seguro (MDN: `NavigatorUAData`). */
  readonly userAgentData?: { readonly platform?: string };
}

/**
 * De lo más fiable a lo menos: `userAgentData.platform` («macOS»), luego
 * `navigator.platform` («MacIntel»; el iPad dice «MacIntel» o «iPad»), y el
 * user agent como último recurso.
 */
export const platformOf = (navigator: PlatformNavigator): Platform => {
  const hinted = navigator.userAgentData?.platform;
  if (hinted !== undefined && hinted !== "") {
    return /^(macOS|iOS|iPadOS)$/i.test(hinted) ? "mac" : "pc";
  }
  const platform = navigator.platform ?? "";
  if (platform !== "") {
    return /^(Mac|iPad|iPhone)/.test(platform) ? "mac" : "pc";
  }
  return /Macintosh|Mac OS X|iPad|iPhone/.test(navigator.userAgent ?? "")
    ? "mac"
    : "pc";
};

/**
 * La de este navegador, una vez. Fuera del navegador (las pruebas en Node)
 * es «pc», aunque se corran en una Mac: Node también tiene `navigator`, y
 * dice «MacIntel» en una.
 */
export const PLATFORM: Platform =
  typeof window === "undefined" ? "pc" : platformOf(window.navigator);
